import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppShell from '@/components/AppShell';
import { FileText, CheckCircle2, AlertTriangle, XCircle, Sparkles, Download, Trash2, Plus, ChevronRight, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

const STATUS_META = {
  met:     { color: 'emerald', Icon: CheckCircle2 },
  partial: { color: 'amber',   Icon: AlertTriangle },
  missing: { color: 'rose',    Icon: XCircle },
};

function GapAnalysisPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState('list'); // 'list' | 'session'
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [standards, setStandards] = useState([]);
  const [permissions, setPermissions] = useState(null);
  const [prefillFromTask, setPrefillFromTask] = useState(null); // {task, standard, clause, files, session}

  useEffect(() => {
    fetchPermissions();
    fetchStandards();
    fetchSessions();
  }, []);

  // Handle ?from_task=XXX — auto-load task mapping and jump to session
  useEffect(() => {
    const fromTask = searchParams.get('from_task');
    if (!fromTask) return;
    (async () => {
      try {
        const token = localStorage.getItem('systemklart_token');
        const r = await axios.get(`${API}/gap-analysis/from-task/${fromTask}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPrefillFromTask(r.data);
        setActiveSession(r.data.session);
        setView('session');
        toast.success(t('gap.prefilledFromTask', 'Forhåndsutfylt fra oppgave'));
        // Clean the query param so reload won't re-trigger
        const p = new URLSearchParams(searchParams);
        p.delete('from_task');
        setSearchParams(p, { replace: true });
      } catch (e) {
        const msg = e.response?.data?.detail || 'Kunne ikke hente koblingen';
        toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
    })();
  }, [searchParams]);

  const fetchPermissions = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.get(`${API}/ops/users/me/permissions`, { headers: { Authorization: `Bearer ${token}` } });
      setPermissions(r.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchStandards = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.get(`${API}/gap-analysis/standards`, { headers: { Authorization: `Bearer ${token}` } });
      setStandards(r.data.standards || []);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.get(`${API}/gap-analysis/sessions`, { headers: { Authorization: `Bearer ${token}` } });
      setSessions(r.data.sessions || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const openSession = async (id) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.get(`${API}/gap-analysis/sessions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setActiveSession(r.data);
      setView('session');
    } catch (e) {
      toast.error('Kunne ikke åpne sesjon');
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm(t('gap.confirmDelete'))) return;
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/gap-analysis/sessions/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('gap.deleted'));
      fetchSessions();
    } catch (e) {
      const msg = e.response?.data?.detail?.message || e.response?.data?.detail || 'Feil';
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    }
  };

  const downloadPdf = async (id) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.get(`${API}/gap-analysis/sessions/${id}/pdf?language=${i18n.language.startsWith('en') ? 'en' : 'no'}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `gap-analyse-${id.slice(0,8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('PDF feilet');
    }
  };

  return (
    <AppShell permissions={permissions}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {view === 'list' && (
          <ListView
            t={t}
            sessions={sessions}
            loading={loading}
            onNew={() => setShowCreate(true)}
            onOpen={openSession}
            onDelete={deleteSession}
            onDownloadPdf={downloadPdf}
          />
        )}
        {view === 'session' && activeSession && (
          <SessionView
            t={t}
            i18n={i18n}
            session={activeSession}
            standards={standards}
            onBack={() => { setView('list'); fetchSessions(); setPrefillFromTask(null); }}
            onSessionUpdated={setActiveSession}
            prefillFromTask={prefillFromTask}
          />
        )}
      </div>
      {showCreate && (
        <CreateSessionModal
          t={t}
          standards={standards}
          onClose={() => setShowCreate(false)}
          onCreated={(sess) => {
            setShowCreate(false);
            setActiveSession(sess);
            setView('session');
            fetchSessions();
          }}
        />
      )}
    </AppShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// LIST VIEW
// ─────────────────────────────────────────────────────────────────────────────
function ListView({ t, sessions, loading, onNew, onOpen, onDelete, onDownloadPdf }) {
  return (
    <>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="gap-page-title">
            <Sparkles className="w-6 h-6 text-cyan-400" />
            {t('gap.title')}
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">{t('gap.subtitle')}</p>
        </div>
        <button
          onClick={onNew}
          className="px-4 py-2 bg-cyan-500 text-slate-950 rounded-lg text-sm font-semibold hover:bg-cyan-400 shadow-lg shadow-cyan-500/25 flex items-center gap-2"
          data-testid="new-gap-session-btn"
        >
          <Plus className="w-4 h-4" /> {t('gap.newSession')}
        </button>
      </div>

      <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-3">{t('gap.sessionList')}</h2>

      {loading ? (
        <div className="py-12 text-center text-slate-500">…</div>
      ) : sessions.length === 0 ? (
        <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-12 text-center" data-testid="empty-gap-list">
          <FileText className="w-10 h-10 mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400">{t('gap.noSessions')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(s => (
            <div key={s.id} className="bg-slate-900/60 border border-slate-800/60 rounded-xl p-4 hover:border-slate-700 transition-all" data-testid={`gap-session-${s.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-200 truncate">{s.title}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {t('gap.createdBy')}: {s.created_by_name} • {new Date(s.created_at).toLocaleDateString()}
                  </p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {s.standards.map(sid => (
                      <span key={sid} className="text-xs px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">
                        {sid.replace('iso_', 'ISO ').replace('_', ':')}
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-2 text-xs">
                    <span className="text-emerald-400">✓ {s.met_count || 0} {t('gap.met')}</span>
                    <span className="text-amber-400">◐ {s.partial_count || 0} {t('gap.partial')}</span>
                    <span className="text-rose-400">✗ {s.missing_count || 0} {t('gap.missing')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onDownloadPdf(s.id)}
                    disabled={!s.result_count}
                    className="px-3 py-1.5 border border-slate-600 text-slate-300 rounded-lg text-sm hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
                    title={t('gap.downloadPdf')}
                    data-testid={`gap-pdf-${s.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onOpen(s.id)}
                    className="px-3 py-1.5 bg-cyan-500 text-slate-950 rounded-lg text-sm font-medium hover:bg-cyan-400 flex items-center gap-1"
                    data-testid={`gap-open-${s.id}`}
                  >
                    {t('gap.open')} <ChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(s.id)}
                    className="p-1.5 text-rose-400 hover:bg-rose-500/10 rounded-lg"
                    title={t('gap.delete')}
                    data-testid={`gap-delete-${s.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CREATE SESSION MODAL
// ─────────────────────────────────────────────────────────────────────────────
function CreateSessionModal({ t, standards, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (selected.length === 0) {
      toast.error(t('gap.standardsRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.post(`${API}/gap-analysis/sessions`, { standards: selected, title: title || null }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === 'pro_required' || detail?.error === 'subscription_required') {
        toast.error(t('gap.proRequired'));
      } else {
        toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Feil'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="gap-create-modal">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-lg w-full">
        <h2 className="text-xl font-bold text-white mb-4">{t('gap.newSession')}</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t('gap.sessionTitle')}</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="GAP-analyse Q2 2026"
              className="w-full bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-200 placeholder-slate-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
              data-testid="gap-session-title-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">{t('gap.chooseStandards')} *</label>
            <div className="space-y-2">
              {standards.map(s => (
                <label
                  key={s.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    selected.includes(s.id)
                      ? 'border-cyan-500 bg-cyan-500/10'
                      : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
                  }`}
                  data-testid={`gap-std-option-${s.id}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(s.id)}
                    onChange={() => toggle(s.id)}
                    className="mt-1 accent-cyan-500"
                  />
                  <div>
                    <p className="font-medium text-slate-200">{s.name}</p>
                    <p className="text-xs text-slate-400">{s.description} • {s.clause_count} klausuler</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 bg-slate-800 border border-slate-700 rounded-xl text-slate-300 hover:bg-slate-700 font-medium" data-testid="gap-cancel-create">
              {t('common.cancel')}
            </button>
            <button type="submit" disabled={submitting || selected.length === 0} className="flex-1 py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-50 shadow-lg shadow-cyan-500/25" data-testid="gap-submit-create">
              {submitting ? '...' : t('gap.start')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SESSION VIEW (clause selector + analyzer + results)
// ─────────────────────────────────────────────────────────────────────────────
function SessionView({ t, i18n, session, standards, onBack, onSessionUpdated, prefillFromTask }) {
  const [allClauses, setAllClauses] = useState([]); // flat: [{standard_id, standard_name, ...clause}]
  const [selectedClause, setSelectedClause] = useState(null);
  const [files, setFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [showCreateTasks, setShowCreateTasks] = useState(null); // result object after analyze
  const [selectedTaskFileIds, setSelectedTaskFileIds] = useState(
    // By default, include all prefilled task files
    (prefillFromTask?.files || []).map(f => f.id)
  );

  useEffect(() => {
    loadAllClauses();
  }, [session.id]);

  // Auto-select the prefilled clause once clauses are loaded
  useEffect(() => {
    if (prefillFromTask && allClauses.length > 0 && !selectedClause) {
      const match = allClauses.find(
        c => c.standard_id === prefillFromTask.standard?.id && c.clause === prefillFromTask.clause?.clause
      );
      if (match) setSelectedClause(match);
    }
  }, [allClauses, prefillFromTask, selectedClause]);

  const loadAllClauses = async () => {
    const token = localStorage.getItem('systemklart_token');
    const flat = [];
    for (const sid of session.standards) {
      try {
        const r = await axios.get(`${API}/gap-analysis/standards/${sid}/clauses`, { headers: { Authorization: `Bearer ${token}` } });
        for (const c of r.data.clauses) {
          flat.push({ standard_id: sid, standard_name: r.data.standard.name, ...c });
        }
      } catch (e) {
        console.error(e);
      }
    }
    setAllClauses(flat);
  };

  const findResult = (std, clause) =>
    (session.results || []).find(r => r.standard_id === std && r.clause === clause);

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const toggleTaskFile = (fileId) => {
    setSelectedTaskFileIds(s =>
      s.includes(fileId) ? s.filter(x => x !== fileId) : [...s, fileId]
    );
  };

  const analyze = async () => {
    if (!selectedClause) return;
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const fd = new FormData();
      fd.append('standard_id', selectedClause.standard_id);
      fd.append('clause', selectedClause.clause);
      fd.append('language', i18n.language.startsWith('en') ? 'en' : 'no');
      for (const f of files) fd.append('files', f);
      if (selectedTaskFileIds.length > 0) {
        fd.append('include_task_file_ids', selectedTaskFileIds.join(','));
      }

      const r = await axios.post(`${API}/gap-analysis/sessions/${session.id}/analyze`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
      toast.success(t('gap.analysisComplete'));
      // Refresh session
      const sess = await axios.get(`${API}/gap-analysis/sessions/${session.id}`, { headers: { Authorization: `Bearer ${token}` } });
      onSessionUpdated(sess.data);
      setFiles([]);
      setShowCreateTasks(r.data.result);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('gap.analyzeFailed'));
    } finally {
      setAnalyzing(false);
    }
  };

  const createTasks = async () => {
    if (!showCreateTasks) return;
    try {
      const token = localStorage.getItem('systemklart_token');
      const r = await axios.post(`${API}/gap-analysis/sessions/${session.id}/create-tasks`,
        { result_ids: [showCreateTasks.id] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('gap.tasksCreated', { count: r.data.tasks_created }));
      const sess = await axios.get(`${API}/gap-analysis/sessions/${session.id}`, { headers: { Authorization: `Bearer ${token}` } });
      onSessionUpdated(sess.data);
      setShowCreateTasks(null);
    } catch (e) {
      toast.error('Kunne ikke opprette oppgaver');
    }
  };

  // Group clauses by standard for the selector
  const grouped = allClauses.reduce((acc, c) => {
    (acc[c.standard_name] = acc[c.standard_name] || []).push(c);
    return acc;
  }, {});

  return (
    <>
      <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-4" data-testid="gap-back-btn">
        <ArrowLeft className="w-4 h-4" /> {t('gap.backToSession')}
      </button>
      <h1 className="text-2xl font-bold text-white mb-1">{session.title}</h1>
      <p className="text-sm text-slate-400 mb-6">
        {session.standards.map(s => s.replace('iso_', 'ISO ').replace('_', ':')).join(' • ')}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT: clause picker */}
        <div className="lg:col-span-1 bg-slate-900/60 border border-slate-800/60 rounded-2xl p-4 max-h-[70vh] overflow-y-auto" data-testid="gap-clause-list">
          <h3 className="font-semibold text-white mb-3">{t('gap.selectClause')}</h3>
          {Object.entries(grouped).map(([stdName, clauses]) => (
            <div key={stdName} className="mb-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{stdName}</p>
              <div className="space-y-1">
                {clauses.map(c => {
                  const result = findResult(c.standard_id, c.clause);
                  const meta = result ? STATUS_META[result.status] : null;
                  const MetaIcon = meta?.Icon;
                  const active = selectedClause?.standard_id === c.standard_id && selectedClause?.clause === c.clause;
                  return (
                    <button
                      key={`${c.standard_id}-${c.clause}`}
                      onClick={() => setSelectedClause(c)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-start gap-2 transition-colors ${
                        active ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30' : 'text-slate-300 hover:bg-slate-800'
                      }`}
                      data-testid={`gap-clause-btn-${c.standard_id}-${c.clause}`}
                    >
                      {MetaIcon ? (
                        <MetaIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 text-${meta.color}-400`} />
                      ) : (
                        <span className="w-4 h-4 mt-0.5 flex-shrink-0 rounded-full border border-slate-600" />
                      )}
                      <span>
                        <span className="font-medium">{c.clause}</span>{' '}
                        {i18n.language.startsWith('en') ? c.title_en : c.title}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* RIGHT: clause detail + upload + result */}
        <div className="lg:col-span-2 space-y-4" data-testid="gap-clause-panel">
          {!selectedClause ? (
            <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-12 text-center text-slate-400">
              {t('gap.selectClause')}
            </div>
          ) : (
            <>
              {prefillFromTask && (
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-xl p-3 flex items-start gap-2" data-testid="gap-prefill-banner">
                  <Sparkles className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-cyan-100">
                    <span className="font-medium">{t('gap.prefilledFromTask')}: </span>
                    <span>{prefillFromTask.task?.title}</span>
                    {prefillFromTask.files?.length > 0 && (
                      <span className="text-cyan-300/70"> • {prefillFromTask.files.length} {t('gap.taskFilesAttached')}</span>
                    )}
                  </div>
                </div>
              )}
              <ClauseDetail t={t} i18n={i18n} clause={selectedClause} />
              {prefillFromTask?.files?.length > 0 && (
                <TaskFilesSelector
                  t={t}
                  files={prefillFromTask.files}
                  selectedIds={selectedTaskFileIds}
                  onToggle={toggleTaskFile}
                />
              )}
              <UploadAndAnalyze
                t={t}
                files={files}
                onFileChange={handleFileChange}
                onAnalyze={analyze}
                analyzing={analyzing}
                canAnalyze={files.length > 0 || selectedTaskFileIds.length > 0}
              />
              {findResult(selectedClause.standard_id, selectedClause.clause) && (
                <ResultPanel
                  t={t}
                  result={findResult(selectedClause.standard_id, selectedClause.clause)}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* Create tasks prompt after analysis */}
      {showCreateTasks && showCreateTasks.suggested_actions?.length > 0 && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="gap-create-tasks-prompt">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-white mb-2">{t('gap.analysisComplete')} ✓</h3>
            <p className="text-sm text-slate-400 mb-4">{t('gap.createTasksQuestion')}</p>
            <div className="bg-slate-800/50 rounded-lg p-3 mb-4 max-h-40 overflow-y-auto">
              {showCreateTasks.suggested_actions.map((a, i) => (
                <div key={i} className="text-xs text-slate-300 mb-1">• {a.title}</div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowCreateTasks(null)} className="flex-1 py-2 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800 text-sm" data-testid="gap-skip-tasks">
                {t('gap.skip')}
              </button>
              <button onClick={createTasks} className="flex-1 py-2 bg-cyan-500 text-slate-950 rounded-lg font-semibold hover:bg-cyan-400 text-sm" data-testid="gap-confirm-create-tasks">
                {t('gap.createTasks')} ({showCreateTasks.suggested_actions.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components: ClauseDetail, UploadAndAnalyze, ResultPanel
// ─────────────────────────────────────────────────────────────────────────────
function ClauseDetail({ t, i18n, clause }) {
  const en = i18n.language.startsWith('en');
  return (
    <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5" data-testid="gap-clause-detail">
      <p className="text-xs text-cyan-400 font-semibold mb-1">{clause.standard_name} — {clause.clause}</p>
      <h2 className="text-lg font-bold text-white mb-3">{en ? clause.title_en : clause.title}</h2>

      <div className="space-y-3 text-sm">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('gap.clauseRequirement')}</p>
          <p className="text-slate-300">{en ? clause.requirement_en : clause.requirement}</p>
        </div>
        {(clause.change_from_2015 || clause.change_from_2015_en) && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-400 mb-1">{t('gap.changeFromPrev')}</p>
            <p className="text-sm text-amber-200">{en ? (clause.change_from_2015_en || clause.change_from_2015) : clause.change_from_2015}</p>
          </div>
        )}
        {clause.evidence_expected?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('gap.evidenceExpected')}</p>
            <div className="flex flex-wrap gap-2">
              {clause.evidence_expected.map((e, i) => (
                <span key={i} className="text-xs px-2 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-300">{e}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function UploadAndAnalyze({ t, files, onFileChange, onAnalyze, analyzing, canAnalyze }) {
  // Allow analyze when either manual files OR task files are available
  const disabled = analyzing || (canAnalyze !== undefined ? !canAnalyze : files.length === 0);
  return (
    <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5">
      <h3 className="font-semibold text-white mb-1">{t('gap.uploadDocs')}</h3>
      <p className="text-xs text-slate-500 mb-3">{t('gap.uploadHelp')}</p>
      <input
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md"
        onChange={onFileChange}
        className="block w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-cyan-500 file:text-slate-950 file:font-semibold hover:file:bg-cyan-400 cursor-pointer"
        data-testid="gap-file-input"
      />
      {files.length > 0 && (
        <p className="text-xs text-slate-400 mt-2">{files.length} fil(er) valgt: {files.map(f => f.name).join(', ')}</p>
      )}
      <button
        onClick={onAnalyze}
        disabled={disabled}
        className="mt-4 w-full py-3 bg-cyan-500 text-slate-950 rounded-xl font-semibold hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/25"
        data-testid="gap-analyze-btn"
      >
        <Sparkles className="w-4 h-4" />
        {analyzing ? t('gap.analyzing') : t('gap.analyze')}
      </button>
    </div>
  );
}

function TaskFilesSelector({ t, files, selectedIds, onToggle }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800/60 rounded-2xl p-5" data-testid="gap-task-files-selector">
      <h3 className="font-semibold text-white mb-1">{t('gap.filesFromTask')}</h3>
      <p className="text-xs text-slate-500 mb-3">{t('gap.filesFromTaskHelp')}</p>
      <div className="space-y-2">
        {files.map(f => {
          const checked = selectedIds.includes(f.id);
          return (
            <label
              key={f.id}
              className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                checked ? 'border-cyan-500 bg-cyan-500/10' : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
              }`}
              data-testid={`gap-task-file-${f.id}`}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => onToggle(f.id)}
                className="accent-cyan-500"
              />
              <FileText className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span className="flex-1 text-sm text-slate-200 truncate">{f.original_filename}</span>
              {f.size && (
                <span className="text-xs text-slate-500 flex-shrink-0">
                  {(f.size / 1024).toFixed(0)} KB
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ResultPanel({ t, result }) {
  const meta = STATUS_META[result.status] || STATUS_META.missing;
  const Icon = meta.Icon;
  return (
    <div className={`bg-slate-900/60 border rounded-2xl p-5 border-${meta.color}-500/30`} data-testid="gap-result-panel">
      <div className="flex items-center gap-3 mb-3">
        <Icon className={`w-6 h-6 text-${meta.color}-400`} />
        <div>
          <p className={`text-sm font-semibold text-${meta.color}-400`}>
            {t(`gap.${result.status}`)} • {t('gap.confidence')}: {result.confidence}%
          </p>
        </div>
      </div>
      <p className="text-sm text-slate-300 mb-4">{result.summary}</p>

      {result.evidence?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{t('gap.evidence')}</p>
          <ul className="space-y-1">
            {result.evidence.map((e, i) => (
              <li key={i} className="text-xs text-slate-400">
                <span className="text-slate-500">[{e.document}]</span> «{e.quote}»
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.gaps?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-rose-400 uppercase tracking-wide mb-1">{t('gap.gaps')}</p>
          <ul className="space-y-1">
            {result.gaps.map((g, i) => <li key={i} className="text-sm text-slate-300">• {g}</li>)}
          </ul>
        </div>
      )}

      {result.suggested_actions?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-cyan-400 uppercase tracking-wide mb-1">{t('gap.suggestedActions')}</p>
          <ul className="space-y-2">
            {result.suggested_actions.map((a, i) => (
              <li key={i} className="bg-slate-800/50 rounded-lg p-3 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-slate-200">{a.title}</p>
                  <span className={`text-xs px-2 py-0.5 rounded flex-shrink-0 ${
                    a.priority === 'high' ? 'bg-rose-500/20 text-rose-400' :
                    a.priority === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-slate-700/50 text-slate-400'
                  }`}>{t(`gap.${a.priority}`)}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{a.description}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.climate_change_note && result.climate_change_alignment !== 'not_applicable' && (
        <div className="mt-4 bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
          <p className="text-xs font-semibold text-blue-400 mb-1">🌍 {t('gap.climateNote')}</p>
          <p className="text-xs text-blue-200">{result.climate_change_note}</p>
        </div>
      )}

      {result.tasks_created && (
        <p className="mt-3 text-xs text-emerald-400">✓ Oppgaver opprettet fra dette resultatet</p>
      )}
    </div>
  );
}

export default GapAnalysisPage;
